import React from 'react';
import { FormikProps } from 'formik';
import { PersonalDetailsDto } from '../../Models';
import { StyledGrid, StyledTextField } from './StyledComponents';
import Gender from '../../../../common/dropdowns/Gender';
import Race from '../../../../common/dropdowns/Race';

interface PersonalInfoFormProps {
  formik: FormikProps<PersonalDetailsDto>;
  personalDetails?: PersonalDetailsDto;
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  formik,
  personalDetails,
}) => {
  // Only lock the form once the step has actually been completed (saved to the server).
  // Returning applicants should see the SAME layout as a brand-new applicant,
  // just with their values prefilled and fully editable so they can update contact info.
  const isDisabled = personalDetails?.isStepCompleted === true;

  // Returning applicants may update contact info (email / phone / etc.) but
  // we never allow them to change the identity number they applied with.
  const isReturningApplicant =
    personalDetails?.isReturningApplicant === true &&
    personalDetails?.hasCurrentYearApplication !== true;
  const isIdentityNumberLocked = isDisabled || isReturningApplicant;

  return (
    <>
      {/* Name */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="name"
          name="name"
          label="Enter your names"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />
      </StyledGrid>

      {/* Surname */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="surname"
          name="surname"
          label="Enter your surname"
          value={formik.values.surname}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          error={formik.touched.surname && Boolean(formik.errors.surname)}
          helperText={formik.touched.surname && formik.errors.surname}
        />
      </StyledGrid>

      {/* Identity Number */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="identityNumber"
          name="identityNumber"
          label="Identity number"
          value={formik.values.identityNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isIdentityNumberLocked}
          error={
            formik.touched.identityNumber &&
            Boolean(formik.errors.identityNumber)
          }
          helperText={
            formik.touched.identityNumber &&
            formik.errors.identityNumber
          }
        />
      </StyledGrid>

      {/* Gender Dropdown */}
      <StyledGrid item xs={12} md={6}>
        <Gender
          formik={formik}
          isDisabled={isDisabled}
        />
      </StyledGrid>

      {/* Race Dropdown */}
      <StyledGrid item xs={12} md={6}>
        <Race
          formik={formik}
          isDisabled={isDisabled}
        />
      </StyledGrid>

      {/* Phone Number */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="phoneNumber"
          name="phoneNumber"
          label="Phone number"
          value={formik.values.phoneNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          error={
            formik.touched.phoneNumber &&
            Boolean(formik.errors.phoneNumber)
          }
          helperText={
            formik.touched.phoneNumber &&
            formik.errors.phoneNumber
          }
        />
      </StyledGrid>

      {/* Email */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="email"
          name="email"
          label="Email address"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </StyledGrid>

      {/* Confirm Email */}
      <StyledGrid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          id="confirmEmail"
          name="confirmEmail"
          label="Confirm email address"
          value={formik.values.confirmEmail}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isDisabled}
          error={
            formik.touched.confirmEmail &&
            Boolean(formik.errors.confirmEmail)
          }
          helperText={
            formik.touched.confirmEmail &&
            formik.errors.confirmEmail
          }
        />
      </StyledGrid>
    </>
  );
};

export default PersonalInfoForm;
